/**
 * 市民端 · 后台可配项的读取（当前只有一条：快速咨询首屏的建议问题）。
 *
 * 接口：`GET /api/citizen/settings/quick-questions`（**公开只读**，无需登录、无需验证码，
 * 与诚信公示 `credit/*` 那些同一类）。出参是市民端信封 `{ ok, data }`，由 `apiGet` 拆。
 *
 * ════════ 为什么这里**破例**做了本地回落 ════════
 *
 * `client.ts` 的第一条纪律写着「不做 mock 回落 —— 市民端回落等于把编的数据
 * 当成协会的答复」。那条纪律针对的是**协会的答复与案件数据**：案件状态、诚信记录、
 * AI 咨询的回答，编一个出来就是骗人。
 *
 * 这里回落的东西不是答复，是**四个可以点的输入建议**。它们的作用只是替市民省下打字，
 * 点下去之后真正的答案照样来自后端、照样带「AI 生成 · 仅供参考」角标。
 * 拉不到就让首屏空一片，损失是实打实的（市民不知道能问什么）；
 * 而回落的代价是零 —— 没有任何一句协会的话被编出来。
 *
 * 所以这条破例的边界是明确的：**只回落建议问题的文案，绝不回落任何答复**。
 *
 * ════════ 回落时不弹提示，但绝不静默 ════════
 *
 * 不弹：这几枚 chip 是锦上添花，为它弹一个失败提示是打扰 —— 市民既不需要知道，
 *       也做不了任何事。
 * 不静默：失败原因走 `console.warn` 打在浏览器控制台，服务端那侧另有 error 日志
 *        （见 `settings.service.ts` 的 `readPublicQuickQuestions`）。两头都留了痕，
 *        排障时查得到「那天为什么显示的是默认四条」。
 */

import { apiGet } from './client';
import { DEFAULT_COMPLAINT_EXAMPLE, DEFAULT_CONSULT_GREETING, DEFAULT_QUICK_QUESTIONS } from '@cdfjx/shared';

/**
 * 建议问题的取数超时。
 *
 * 比 `client.ts` 的默认 8 秒短得多：这是一个**装饰性**请求，等它 8 秒等于让首屏
 * 那一块空白挂 8 秒。4 秒还没回来，就按拉不到处理、显示兜底四条 ——
 * 对市民来说，一直空着比晚一点显示更糟。
 */
const TIMEOUT_MS = 4000;

/**
 * 咨询页首屏要的两样：**开场白 ＋ 建议问题**（2026-09-19 开场白也改成后台可配）。
 *
 * ★ 合成一次请求，不是两次：这两样同屏同时用，分两次会出现「话有了、问题没有」
 *   的半截首屏，而且多一次往返、多一套超时。
 * ★ 回落边界与建议问题那条**完全一致**：回落的只有「开场白文案」与「输入建议」，
 *   **绝不回落任何一句协会的答复**。文件头那段理由逐字适用于开场白 ——
 *   它同样不是答复，只是一句自我介绍。
 */
export interface ConsultSettingsFetch {
  greeting: string;
  questions: string[];
  /** true = 没拉到，用的是兜底文案与兜底四条 */
  fromFallback: boolean;
  reason?: string;
}

interface PublicConsultWire {
  greeting?: unknown;
  questions?: unknown;
  updatedAt?: unknown;
}

/** 拉开场白＋建议问题。**永不抛异常**。 */
export async function fetchConsultSettings(): Promise<ConsultSettingsFetch> {
  const fallback = (reason: string): ConsultSettingsFetch => {
    // eslint-disable-next-line no-console
    console.warn(`[快速咨询] 首屏配置未能从后台取回，本次显示系统默认文案：${reason}`);
    return {
      greeting: DEFAULT_CONSULT_GREETING,
      questions: [...DEFAULT_QUICK_QUESTIONS],
      fromFallback: true,
      reason,
    };
  };

  try {
    const data = await apiGet<PublicConsultWire>('/api/citizen/settings/consult', TIMEOUT_MS);
    const list = data?.questions;
    const greeting = data?.greeting;
    /* 两样各判各的形状，但**只要有一样不合契约就整体回落**：
       半截配置渲染出来的首屏，比晚半秒的默认首屏更难解释。 */
    if (typeof greeting !== 'string' || greeting.trim() === '') {
      return fallback('返回体形状与契约不符（greeting 不是非空文本）');
    }
    if (!Array.isArray(list) || list.some((x) => typeof x !== 'string')) {
      return fallback('返回体形状与契约不符（questions 不是字符串数组）');
    }
    return { greeting, questions: list as string[], fromFallback: false };
  } catch (e) {
    return fallback(e instanceof Error ? e.message : String(e));
  }
}

/**
 * 投诉表单下面那块「示范」文字（2026-09-23 改成后台可配）。**永不抛异常**。
 *
 * ★ 回落边界同上：示范是一段**举例的话**，不是协会的答复。拉不到就显示改前写死的那段，
 *   损失为零；让表单下面空一块或弹错，反而是真损失。
 * ★ 模块级缓存：手机端与 PC 端、以及用户在表单页来回进出，全程只打一次。
 *   失败的那次不缓存 —— 下次进页面还有机会拿到协会配的版本。
 */
let exampleCache: Promise<string> | null = null;

export function fetchComplaintExample(): Promise<string> {
  if (exampleCache) return exampleCache;
  const p = (async (): Promise<string> => {
    try {
      const data = await apiGet<{ example?: unknown }>('/api/citizen/settings/complaint-example', TIMEOUT_MS);
      const ex = data?.example;
      if (typeof ex !== 'string' || ex.trim() === '') {
        throw new Error('返回体形状与契约不符（example 不是非空文本）');
      }
      return ex;
    } catch (e) {
      exampleCache = null;
      // eslint-disable-next-line no-console
      console.warn(
        `[投诉表单] 示范文字未能从后台取回，本次显示系统默认文案：${e instanceof Error ? e.message : String(e)}`,
      );
      return DEFAULT_COMPLAINT_EXAMPLE;
    }
  })();
  exampleCache = p;
  return p;
}
